/**
 * Copyright (c) 2026 Spicy Friends Ltd.
 * All rights reserved.
 *
 * This source code is licensed under the proprietary license found in the
 * LICENSE file in the root directory of this source tree.
 */

'use client'

import type { ComponentProps } from 'react'
import { forwardRef, useId } from 'react'
import type { RegisterOptions } from 'react-hook-form'
import { useFormContext, useFormState } from 'react-hook-form'

import { cn, mergeRefs } from '../../../lib/utils'
import { Field, FieldDescription, FieldError, FieldLabel } from '../../ui/field'
import { Textarea } from '../../ui/textarea'

export type TextAreaFormFieldProps = {
  autoComplete?: ComponentProps<'textarea'>['autoComplete']
  disabled?: boolean
  fieldProps?: ComponentProps<typeof Field>
  helperText?: string
  label?: string
  name: string
  placeholder?: string
  required?: boolean
  textareaProps?: ComponentProps<typeof Textarea>
  validation?: RegisterOptions
}

export const TextAreaFormField = forwardRef<HTMLTextAreaElement, TextAreaFormFieldProps>((props, forwardedRef) => {
  const { register, control } = useFormContext()
  const { errors } = useFormState({ control, name: props.name })

  const { ref, ...workRegister } = register(props.name, props.validation)

  const inputId = useId()

  const { textareaProps } = props
  const { ref: taRef, className: taClassName, ...restTextareaProps } = textareaProps ?? {}

  const error = errors[props.name]
  const invalid = !!error

  return (
    <Field {...props.fieldProps} data-invalid={invalid}>
      {props.label !== undefined && (
        <FieldLabel className="flex items-center gap-1" htmlFor={inputId}>
          {props.label}
          {props.required ? (
            <span aria-hidden className="text-destructive">
              *
            </span>
          ) : null}
        </FieldLabel>
      )}
      <Textarea
        {...workRegister}
        {...restTextareaProps}
        aria-invalid={invalid}
        autoComplete={props.autoComplete}
        className={cn(taClassName)}
        disabled={props.disabled}
        id={inputId}
        placeholder={props.placeholder}
        ref={mergeRefs(ref, forwardedRef, taRef)}
        required={props.required}
      />
      {props.helperText !== undefined ? <FieldDescription>{props.helperText}</FieldDescription> : null}
      {error?.message ? <FieldError>{String(error.message)}</FieldError> : null}
    </Field>
  )
})

TextAreaFormField.displayName = 'TextAreaFormField'
